import { Box, Button, type ButtonProps, Dialog, Text } from '@chakra-ui/react'
import React, { useRef } from 'react'

import { Modal } from './Modal'

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when the dialog should close (cancel, backdrop, escape) */
  onClose: () => void
  /** Title shown in the header */
  title: string
  /** Description or body text; pass empty string if using children for the full body */
  description?: string
  /** Label for the confirm button */
  confirmLabel: string
  /** Label for the cancel button */
  cancelLabel: string
  /** Callback when the user confirms */
  onConfirm: () => void
  /** Visual style of the confirm button: default (neutral) or destructive (red) */
  variant?: 'default' | 'destructive'
  /** Optional color palette override for the confirm button. Takes precedence over variant. */
  confirmColorPalette?: ButtonProps['colorPalette']
  /** Whether the confirm action is in progress (disables buttons, shows loading on confirm) */
  isLoading?: boolean
  /** Whether the confirm button should be disabled (e.g. waiting for user selection) */
  confirmDisabled?: boolean
  /** Max width of the dialog. Defaults to 400px. */
  maxWidth?: string | number
  /** Button text shown while `isLoading` is true. */
  confirmLoadingText?: string
  /** ARIA role. Destructive confirms default to alertdialog. */
  role?: 'dialog' | 'alertdialog'
  /** Accessible name for the header close button */
  closeLabel?: string
  /** Stacking order passed to Modal */
  zIndex?: number
  /** Optional extra content rendered between description and footer */
  children?: React.ReactNode
}

/**
 * Confirmation dialog built on Modal.
 * Use for confirm/cancel flows (e.g. delete confirmation).
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  variant = 'default',
  confirmColorPalette,
  isLoading = false,
  confirmDisabled = false,
  maxWidth = '400px',
  confirmLoadingText,
  role,
  closeLabel,
  zIndex,
  children,
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const resolvedRole =
    role ?? (variant === 'destructive' ? 'alertdialog' : undefined)

  const footer = (
    <Box display="flex" gap={3} justifyContent="flex-end">
      <Button
        ref={cancelButtonRef}
        type="button"
        variant="outline"
        colorPalette="neutral"
        onClick={onClose}
        px={5}
        py={2.5}
        borderRadius="lg"
        borderColor="border.default"
        _hover={{
          borderColor: 'border.emphasized',
          bg: 'bg.subtle',
        }}
        disabled={isLoading}
      >
        {cancelLabel}
      </Button>
      <Button
        type="button"
        colorPalette={
          confirmColorPalette ??
          (variant === 'destructive' ? 'error' : 'neutral')
        }
        onClick={onConfirm}
        loading={isLoading}
        loadingText={confirmLoadingText}
        disabled={confirmDisabled}
        px={5}
        py={2.5}
        borderRadius="lg"
      >
        {confirmLabel}
      </Button>
    </Box>
  )

  return (
    <Modal
      open={open}
      onOpenChange={(details) => !details.open && onClose()}
      title={title}
      footer={footer}
      maxWidth={maxWidth}
      role={resolvedRole}
      initialFocusEl={
        resolvedRole === 'alertdialog'
          ? () => cancelButtonRef.current
          : undefined
      }
      closeLabel={closeLabel}
      closeDisabled={isLoading}
      zIndex={zIndex}
    >
      <Dialog.Description asChild>
        <Box display="flex" flexDirection="column" gap={3} color="inherit">
          {description ? <Text>{description}</Text> : null}
          {children}
        </Box>
      </Dialog.Description>
    </Modal>
  )
}

ConfirmDialog.displayName = 'ConfirmDialog'
